import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Film, Tv, Trophy, Swords, Eye, TrendingUp, FileText, RefreshCw, Calendar } from "lucide-react";

const AdminSeoStatsSection = () => {
  const [period, setPeriod] = useState("30");
  const [topPageType, setTopPageType] = useState("all");

  // Fetch total pages count
  const { data: pageCounts } = useQuery({
    queryKey: ["seo-page-counts"],
    queryFn: async () => {
      const [movies, series, football, ufc] = await Promise.all([
        supabase.from("movies").select("id", { count: "exact", head: true }).eq("published", true),
        supabase.from("series").select("id", { count: "exact", head: true }).eq("published", true),
        supabase.from("football_matches").select("id", { count: "exact", head: true }).eq("published", true),
        supabase.from("ufc_fights").select("id", { count: "exact", head: true }).eq("published", true),
      ]);
      return {
        movies: movies.count || 0,
        series: series.count || 0,
        football: football.count || 0,
        ufc: ufc.count || 0,
        total: (movies.count || 0) + (series.count || 0) + (football.count || 0) + (ufc.count || 0),
      };
    },
    refetchInterval: 60000,
  });

  // Fetch daily views for chart
  const { data: dailyViews, isLoading: viewsLoading } = useQuery({
    queryKey: ["seo-daily-views", period],
    queryFn: async () => {
      const daysAgo = parseInt(period);
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - daysAgo);
      const fromStr = fromDate.toISOString().split("T")[0];

      const { data, error } = await supabase
        .from("seo_page_views")
        .select("view_date, views_count, page_type")
        .gte("view_date", fromStr)
        .order("view_date", { ascending: true });

      if (error) throw error;

      // Group by date
      const byDate: Record<string, { date: string; films: number; series: number; football: number; ufc: number; total: number }> = {};
      for (const row of data || []) {
        if (!byDate[row.view_date]) {
          byDate[row.view_date] = { date: row.view_date, films: 0, series: 0, football: 0, ufc: 0, total: 0 };
        }
        const d = byDate[row.view_date];
        const v = row.views_count;
        d.total += v;
        if (row.page_type === "movie") d.films += v;
        else if (row.page_type === "series") d.series += v;
        else if (row.page_type === "football") d.football += v;
        else if (row.page_type === "ufc") d.ufc += v;
      }

      return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
    },
  });

  // Top pages
  const { data: topPages } = useQuery({
    queryKey: ["seo-top-pages", period, topPageType],
    queryFn: async () => {
      const daysAgo = parseInt(period);
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - daysAgo);
      const fromStr = fromDate.toISOString().split("T")[0];

      let query = supabase
        .from("seo_page_views")
        .select("page_type, page_slug, page_title, views_count, view_date")
        .gte("view_date", fromStr);

      if (topPageType !== "all") {
        query = query.eq("page_type", topPageType);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Aggregate by slug
      const bySlug: Record<string, { page_type: string; page_slug: string; page_title: string | null; total_views: number }> = {};
      for (const row of data || []) {
        const key = `${row.page_type}:${row.page_slug}`;
        if (!bySlug[key]) {
          bySlug[key] = { page_type: row.page_type, page_slug: row.page_slug, page_title: row.page_title, total_views: 0 };
        }
        bySlug[key].total_views += row.views_count;
      }

      return Object.values(bySlug)
        .sort((a, b) => b.total_views - a.total_views)
        .slice(0, 20);
    },
  });

  // Today's total
  const todayTotal = dailyViews?.find(d => d.date === new Date().toISOString().split("T")[0])?.total || 0;
  const totalViews = dailyViews?.reduce((sum, d) => sum + d.total, 0) || 0;

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "movie": return <Film className="w-4 h-4 text-primary" />;
      case "series": return <Tv className="w-4 h-4 text-blue-400" />;
      case "football": return <Trophy className="w-4 h-4 text-green-400" />;
      case "ufc": return <Swords className="w-4 h-4 text-red-400" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getTypeRoute = (type: string) => {
    switch (type) {
      case "movie": return "films";
      case "series": return "series";
      case "football": return "football";
      case "ufc": return "ufc";
      default: return "";
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
  };

  return (
    <div className="space-y-4">
      {/* Period selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Dashboard SEO
        </h2>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[160px]">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 derniers jours</SelectItem>
            <SelectItem value="30">30 derniers jours</SelectItem>
            <SelectItem value="90">3 mois</SelectItem>
            <SelectItem value="365">1 an</SelectItem>
            <SelectItem value="9999">Tout</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="bg-card border-border">
          <CardContent className="p-3 text-center">
            <FileText className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-2xl font-bold text-foreground">{pageCounts?.total || 0}</p>
            <p className="text-xs text-muted-foreground">Pages publiées</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-3 text-center">
            <Eye className="w-5 h-5 mx-auto mb-1 text-secondary" />
            <p className="text-2xl font-bold text-foreground">{todayTotal}</p>
            <p className="text-xs text-muted-foreground">Vues aujourd'hui</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-3 text-center">
            <TrendingUp className="w-5 h-5 mx-auto mb-1 text-green-400" />
            <p className="text-2xl font-bold text-foreground">{totalViews}</p>
            <p className="text-xs text-muted-foreground">Vues totales</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-3 text-center">
            <Film className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-2xl font-bold text-foreground">{pageCounts?.movies || 0}</p>
            <p className="text-xs text-muted-foreground">Films</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-3 text-center">
            <Tv className="w-5 h-5 mx-auto mb-1 text-blue-400" />
            <p className="text-2xl font-bold text-foreground">{pageCounts?.series || 0}</p>
            <p className="text-xs text-muted-foreground">Séries</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-3 text-center">
            <Trophy className="w-5 h-5 mx-auto mb-1 text-green-400" />
            <p className="text-2xl font-bold text-foreground">{(pageCounts?.football || 0) + (pageCounts?.ufc || 0)}</p>
            <p className="text-xs text-muted-foreground">Sport</p>
          </CardContent>
        </Card>
      </div>

      {/* Views chart */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-foreground">Vues par jour</CardTitle>
        </CardHeader>
        <CardContent>
          {viewsLoading ? (
            <div className="h-48 flex items-center justify-center text-muted-foreground">Chargement...</div>
          ) : dailyViews && dailyViews.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dailyViews}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }}
                  labelFormatter={formatDate}
                />
                <Bar dataKey="films" stackId="a" fill="hsl(var(--primary))" name="Films" radius={[0, 0, 0, 0]} />
                <Bar dataKey="series" stackId="a" fill="#60a5fa" name="Séries" />
                <Bar dataKey="football" stackId="a" fill="#4ade80" name="Football" />
                <Bar dataKey="ufc" stackId="a" fill="#f87171" name="UFC" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
              Aucune vue enregistrée sur cette période
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top pages */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base text-foreground">Top pages</CardTitle>
          <Select value={topPageType} onValueChange={setTopPageType}>
            <SelectTrigger className="w-[120px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="movie">Films</SelectItem>
              <SelectItem value="series">Séries</SelectItem>
              <SelectItem value="football">Football</SelectItem>
              <SelectItem value="ufc">UFC</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Page</TableHead>
                  <TableHead className="text-xs text-right w-20">Vues</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topPages && topPages.length > 0 ? (
                  topPages.map((page, i) => (
                    <TableRow key={i}>
                      <TableCell className="py-2">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(page.page_type)}
                          <a
                            href={`/${getTypeRoute(page.page_type)}/${page.page_slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-foreground hover:text-primary truncate max-w-[250px] sm:max-w-[400px]"
                            title={page.page_title || page.page_slug}
                          >
                            {page.page_title || page.page_slug}
                          </a>
                        </div>
                      </TableCell>
                      <TableCell className="text-right py-2">
                        <Badge variant="secondary" className="text-xs font-mono">
                          {page.total_views}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center text-muted-foreground text-sm py-6">
                      Aucune donnée
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSeoStatsSection;
